// Ο κατάλογος των γεγονότων του συστήματος, σε ΕΝΑ σημείο.
//
// Το συμβόλαιο δραστηριότητας (changelog, P1-7) απαιτεί οι τύποι να οριστούν μία
// φορά και να τους διαβάζουν και οι δύο καταναλωτές: η ροή δραστηριότητας (P1-7)
// και τα webhooks (P2-1). Δύο κατάλογοι θα αποκλίνουν — η μία πλευρά θα λέει
// `task.completed` και η άλλη `task.done`, και κανείς δεν θα το δει μέχρι να μη
// φτάσει ένα webhook. Είναι η κατηγορία ΣΤ του Κεφαλαίου 6.
//
// Ο κατάλογος είναι **πλήρης από τώρα**, με γεγονότα που ακόμη δεν εκπέμπει
// κανείς. Γράφεται πριν από τα P1-3, P1-4 και P2-1 και όχι μαζί τους, γιατί τρεις
// κλάδοι που γράφουν παράλληλα τον ίδιο κατάλογο παράγουν τρεις εκδοχές που
// συμφωνούν κατά λάθος: η ένωση περνά καθαρή και η απόκλιση φαίνεται μόνο στην
// εκτέλεση.
//
// Κάθε εργασία **προσθέτει μόνο εκπομπές** — `activityService.log(...)` και
// `webhookService.dispatch(...)` — και δεν επεξεργάζεται αυτό το αρχείο. Αν
// χρειαστεί να το επεξεργαστεί, ή λείπει γεγονός από το συμβόλαιο ή η εργασία
// βγήκε εκτός πεδίου· και στις δύο περιπτώσεις η απόφαση παίρνεται ρητά.

/**
 * Οι δύο κατηγορίες γεγονότος, από το σκέλος δ του συμβολαίου.
 *
 * Το κρίσιμο δεν είναι η ονομασία αλλά η **προεπιλογή**: εσωτερικό είναι ό,τι δεν
 * έχει δηλωθεί ρητά ως εξωτερικεύσιμο. Γεγονός ιδιωτικής συλλογής που φεύγει σε
 * webhook τρίτου είναι διαρροή που **δεν φαίνεται πουθενά στη διεπαφή**, γιατί ο
 * παραλήπτης δεν είναι χρήστης του συστήματος: δεν υπάρχει οθόνη όπου κάποιος θα
 * το πρόσεχε. Η κατηγορία είναι ιδιότητα του **τύπου** και απαντά στο «μπορεί
 * ποτέ να φύγει;». Το «φεύγει τώρα;» το κρίνει επιπλέον η `mayLeaveSystem`.
 */
export const SCOPES = Object.freeze({
  INTERNAL: 'internal',
  EXTERNALISABLE: 'externalisable'
});

/**
 * Τα πεδία που επιστρέφει η `visibleJson` ανά τύπο αντικειμένου: το **ανώτατο**
 * όριο κάθε ωφέλιμου φορτίου.
 *
 * Το `object` κάθε γεγονότος περνά από την ίδια `visibleJson` που χρησιμοποιούν οι
 * διαδρομές ανάγνωσης, με τα δικαιώματα του κατόχου του webhook, και **ποτέ δεν
 * είναι ολόκληρο το έγγραφο**. Χωρίς αυτό το webhook γίνεται η πόρτα από την
 * οποία βγαίνει ό,τι φυλάει η διαδρομή ανάγνωσης: το `collections[]` ενός κοινού
 * πόρου σε παραλήπτη που δεν βλέπει όλες τις συλλογές είναι ακριβώς το
 * αντιστάθμισμα της D9 για τη ΜΛΑ-09, παρακαμμένο από το δεύτερο κανάλι εξόδου.
 *
 * Τα `resource` και `collection` **ελέγχονται από τη δοκιμή απέναντι στα σχήματα
 * Mongoose**: πεδίο που προστίθεται στο μοντέλο και όχι εδώ ρίχνει τη σουίτα.
 * Είναι σκόπιμο. Όταν το P2-3 προσθέσει το `funding`, κάποιος θα αποφασίσει ρητά
 * αν οι όροι μιας πρόσκλησης βγαίνουν σε τρίτο σύστημα, αντί να βγουν επειδή έτυχε.
 *
 * Το `user` είναι **στενότερο από το `toJSON` του μοντέλου**: είναι η προβολή
 * `username fullName avatar` που ήδη χρησιμοποιεί κάθε `populate` των controllers.
 * Το `email` δεν φεύγει ποτέ σε γεγονός.
 *
 * Τα `comment`, `rating` και `task` δηλώνονται εδώ **πριν** υπάρξουν τα μοντέλα,
 * κατά το μοντέλο-στόχο του `CLAUDE.md`. Τα P1-3 και P1-4 τα επαληθεύουν όταν
 * γράψουν τα σχήματα.
 */
export const VISIBLE_FIELDS = Object.freeze({
  resource: Object.freeze([
    'id', 'collections', 'url', 'title', 'description', 'image', 'tags', 'funding',
    'relatedResources', 'createdBy', 'createdAt', 'updatedAt'
  ]),
  collection: Object.freeze([
    'id', 'title', 'description', 'topic', 'visibility', 'archived', 'archivedAt',
    'members', 'createdBy', 'createdAt', 'updatedAt'
  ]),
  user: Object.freeze(['id', 'username', 'fullName', 'avatar']),
  comment: Object.freeze(['id', 'resource', 'collection', 'author', 'text', 'createdAt']),
  rating: Object.freeze([
    'id', 'resource', 'collection', 'user', 'relevance', 'justification', 'createdAt'
  ]),
  // P1-8. Το φορτίο **δεν περιέχει ούτε το μυστικό ούτε το αποτύπωμά του**: το
  // πρώτο δεν υπάρχει πουθενά μετά τη δημιουργία, το δεύτερο δεν έχει λόγο να
  // ταξιδέψει. Η ισότητα με την προβολή του μοντέλου ελέγχεται στο
  // `tests/events.test.js`, οπότε ένα `keyHash` που θα έμπαινε κατά λάθος στα
  // ορατά πεδία ρίχνει τη σουίτα.
  apikey: Object.freeze([
    'id', 'label', 'collection', 'createdBy', 'revoked', 'lastUsedAt',
    'createdAt', 'updatedAt'
  ]),
  task: Object.freeze([
    'id', 'collection', 'resource', 'title', 'description', 'assignees', 'status',
    'dueDate', 'notes', 'createdBy', 'createdAt', 'updatedAt'
  ]),

  /**
   * **Ο τύπος των γεγονότων διαγραφής, και δεν είναι το έγγραφο.**
   *
   * Ένα γεγονός διαγραφής δεν μπορεί να φέρει `visibleJson` του αντικειμένου του:
   * το αντικείμενο **δεν υπάρχει πια**. Ό,τι απομένει είναι μια εγγραφή αφαίρεσης —
   * τι ήταν, και **τι χάθηκε μαζί του**.
   *
   * Το δεύτερο μισό είναι ο λόγος που ο τύπος είναι ξεχωριστός. Με τη **D13** η
   * διαγραφή παίρνει μαζί της τις κρίσεις της συλλογής, δηλαδή δουλειά που συχνά
   * ανήκει σε **άλλους**. Ίχνος που λέει «διαγράφηκε» χωρίς να λέει **τι κόστισε**
   * είναι μισό ίχνος: καταγράφει το γεγονός και αποκρύπτει το μέγεθός του.
   *
   * Δεν έχει μοντέλο, οπότε δεν ελέγχεται απέναντι σε σχήμα — σωστά: δεν είναι
   * οντότητα που αποθηκεύεται, είναι το αποτύπωμα μιας που έφυγε.
   */
  /**
   * Η συνδρομή εξωτερικής ειδοποίησης, **χωρίς το μυστικό υπογραφής**.
   *
   * Το `secret` λείπει σκόπιμα και είναι το μόνο πεδίο που έχει σημασία εδώ: μια
   * καταγραφή ασφαλείας που αντιγράφει το μυστικό μέσα της μετατρέπει τη ροή
   * δραστηριότητας σε δεύτερο σημείο διαρροής του — και μάλιστα σε σημείο που
   * διαβάζεται από περισσότερους ανθρώπους από ό,τι η ίδια η συνδρομή.
   */
  webhook: Object.freeze(['id', 'url', 'events', 'collection', 'active', 'owner', 'createdAt']),

  removal: Object.freeze([
    'id', 'title', 'url',
    'deletedResources', 'deletedComments', 'deletedRatings'
  ])
});

/**
 * Ο κατάλογος. Μία εγγραφή ανά γραμμή του πίνακα του σκέλους α.
 *
 * `objectType` — τι είναι το αντικείμενο, άρα ποιο σύνολο της `VISIBLE_FIELDS` το ορίζει.
 * `scope`      — αν ο τύπος μπορεί ποτέ να φύγει από το σύστημα.
 * `payload`    — τα πεδία του `object`, ονομαστικά. Υποσύνολο της `VISIBLE_FIELDS[objectType]`.
 * `task`       — ποια εργασία γράφει την εκπομπή.
 *
 * Ο **φάκελος** είναι κοινός και δεν επαναλαμβάνεται ανά γεγονός:
 *
 *   { event, timestamp,
 *     actor:      { id, username },
 *     collection: { id, title },
 *     object:     τα πεδία της `payload`, περασμένα από τη visibleJson }
 *
 * Το `summary` της εγγραφής `Activity` **δεν είναι μέρος του φορτίου**: είναι
 * ανθρώπινο κείμενο για τη ροή δραστηριότητας. Γι' αυτό η παλιά κατάσταση στο
 * `task.status_changed` και το πλήθος των διαγραμμένων κρίσεων στο
 * `resource.detached` ζουν εκεί και όχι εδώ — το `object` φέρει την **τρέχουσα**
 * κατάσταση του αντικειμένου, όχι τη μεταβολή.
 */
export const EVENT_CATALOG = Object.freeze({
  // --- Πόροι (P1-2, οι ενέργειες υπάρχουν ήδη) ---------------------------------

  'resource.created': Object.freeze({
    objectType: 'resource',
    scope: SCOPES.EXTERNALISABLE,
    payload: Object.freeze([
      'id', 'url', 'title', 'description', 'image', 'tags', 'collections',
      'createdBy', 'createdAt'
    ]),
    task: 'P1-2'
  }),

  // Εσωτερικό, και ο λόγος είναι ο ίδιος που το κρατά έξω από τις υπόλοιπες
  // συλλογές του πόρου: η προσάρτηση λέει ότι η διεύθυνση **προϋπήρχε αλλού**.
  // Είναι η συναγωγή ύπαρξης που η D9 δεσμεύεται να μη διαρρέει, και σε τρίτο
  // παραλήπτη διαρρέει χωρίς κανένα αντιστάθμισμα.
  'resource.attached': Object.freeze({
    objectType: 'resource',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'url', 'title', 'collections']),
    task: 'P1-2'
  }),

  // Καταγράφεται σε ΟΛΕΣ τις συλλογές του πόρου: είναι το τίμημα της D9, ο
  // εμπλουτισμός από τη μία ομάδα αλλάζει την κάρτα των υπολοίπων.
  /**
   * ΛΑ-12: δηλώθηκε σχέση ανάμεσα σε δύο πόρους.
   *
   * **Εσωτερικό**, με το ίδιο σκεπτικό που έβγαλε τα `comment.created` και
   * `rating.created` από τα εξωτερικεύσιμα — αλλά με δικό του λόγο, που αξίζει
   * να γραφτεί: το γεγονός αφορά **δύο** πόρους, οι οποίοι μπορεί να ανήκουν σε
   * συλλογές **διαφορετικής ορατότητας**. Το φορτίο θα έπρεπε να ονομάσει και
   * τα δύο, και η ερώτηση «ποιου την ορατότητα τηρούμε;» δεν έχει απάντηση όταν
   * ο παραλήπτης είναι ένα τρίτο σύστημα με μία συνδρομή.
   *
   * Στην **ανάγνωση** το ερώτημα λύνεται: το φίλτρο ορατότητας κόβει ανά αιτούντα.
   * Στην **αποστολή** δεν υπάρχει αιτών — υπάρχει προορισμός.
   */
  'resource.related': Object.freeze({
    objectType: 'resource',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'title', 'relatedResources', 'updatedAt']),
    task: 'P3-2'
  }),

  'resource.updated': Object.freeze({
    objectType: 'resource',
    scope: SCOPES.EXTERNALISABLE,
    payload: Object.freeze([
      'id', 'url', 'title', 'description', 'image', 'tags', 'collections', 'updatedAt'
    ]),
    task: 'P1-2'
  }),

  // **Το αντίγραφο για τις ΥΠΟΛΟΙΠΕΣ συλλογές του κοινού πόρου (P1-7).**
  //
  // Η μοναδική προσθήκη στον κατάλογο μετά τη διακήρυξή του ως πλήρους, με ρητή
  // έγκριση, και ο λόγος της είναι **αντίφαση μέσα στο ίδιο το συμβόλαιο**: το
  // σκέλος α απαιτεί το `resource.updated` να καταγράφεται σε **ΟΛΕΣ** τις
  // συλλογές του πόρου, ενώ ο κανόνας Μ1 απαγορεύει το `collections[]` ως
  // υποκατάστατο του πλαισίου της ενέργειας. Και οι δύο έχουν δίκιο, και δεν
  // συμβιβάζονται: το `resource.updated` είναι **εξωτερικεύσιμο**, οπότε γραφή
  // του σε όλες τις συλλογές μέσα από τη μία αδιαίρετη διαδρομή εκπομπής
  // σημαίνει **παράδοση webhook σε όλες τους** — ακριβώς η Μ1.
  //
  // Η διέξοδος είναι σημασιολογική και όχι τεχνική. **«Κάτι που σε αφορά άλλαξε
  // ΑΛΛΟΥ» δεν είναι το ίδιο γεγονός με «κάτι άλλαξε ΕΔΩ».** Η συλλογή όπου έγινε
  // η επεξεργασία βλέπει `resource.updated`· οι υπόλοιπες βλέπουν αυτό, με
  // διαφορετική διατύπωση στην οθόνη, ώστε ο αναγνώστης να μη νομίσει ότι η
  // επεξεργασία έγινε στη δική του ομάδα.
  //
  // Εσωτερικό **χωρίς εξέταση**: είναι η ίδια πληροφορία ιδωμένη από ομάδα που
  // δεν ενήργησε. Η εξωτερίκευσή της θα ήταν ακριβώς η Μ1 από την πίσω πόρτα.
  //
  // **Το φορτίο δεν φέρει `collections`**, σε αντίθεση με το `resource.updated`.
  // Δεν είναι παράλειψη: η εγγραφή λέει ότι ο πόρος άλλαξε κάπου αλλού, και ο
  // χάρτης των συλλογών του —έστω φιλτραρισμένος— είναι το ένα βήμα που μένει
  // για να μαντέψει κανείς πού. Είναι η συναγωγή ύπαρξης που φυλάει η D9.
  //
  // **Παραλήπτες: κανείς.** Είναι ροή και όχι ειδοποίηση — δεν απευθύνεται σε
  // πρόσωπο, κατά τον κανόνα του `lib/recipients.js`.
  //
  // Δεν το εκπέμπει controller: **παράγεται** μέσα στην `activityService.log`
  // από το `resource.updated`, γιατί ο controller έχει —σωστά— μία μόνο συλλογή
  // στο πλαίσιό του. Ο φράχτης του `tests/emission.test.js` το γνωρίζει ρητά.
  'resource.updated_elsewhere': Object.freeze({
    objectType: 'resource',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze([
      'id', 'url', 'title', 'description', 'image', 'tags', 'updatedAt'
    ]),
    task: 'P1-7'
  }),

  // Εσωτερικό: το `summary` φέρει το πλήθος των κρίσεων που διαγράφηκαν (D13),
  // δηλαδή μέγεθος της δραστηριότητας μιας ομάδας πάνω σε έναν πόρο.
  'resource.detached': Object.freeze({
    objectType: 'resource',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'url', 'title']),
    task: 'P1-2'
  }),

  // Το έγγραφο του πόρου **έπαψε να υπάρχει** — όχι απλώς αποσυνδέθηκε. Συμβαίνει
  // όταν αφαιρεθεί από την τελευταία του συλλογή, οπότε εκπέμπεται **μαζί** με το
  // `resource.detached`: δύο διακριτά γεγονότα για την ίδια ενέργεια, γιατί
  // «αφαιρέθηκε από αυτή τη συλλογή» και «δεν υπάρχει πλέον πουθενά» είναι
  // διαφορετικές πληροφορίες, και μόνο η δεύτερη είναι οριστική.
  //
  // Εσωτερικό, και εδώ ο λόγος **δεν** είναι δομικός — αυτό το γεγονός θα ήταν
  // απολύτως επιδόσιμο. Είναι λόγος συνέπειας: το φορτίο φέρει το πλήθος των
  // κρίσεων που χάθηκαν, και ο κατάλογος έχει **ήδη** κρίνει ότι τα πλήθη κρίσεων
  // δεν φεύγουν — γι' αυτό ακριβώς είναι εσωτερικό το `resource.detached`. Το να
  // εξωτερικευόταν εδώ το ίδιο μέγεθος θα ακύρωνε εκείνη την απόφαση από την πίσω
  // πόρτα.
  'resource.deleted': Object.freeze({
    objectType: 'removal',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'title', 'url', 'deletedComments', 'deletedRatings']),
    task: 'P1-2'
  }),

  // --- Σχόλια και αξιολογήσεις (P1-3) ------------------------------------------
  //
  // **Πάντοτε εσωτερικά**, ανεξαρτήτως ορατότητας της συλλογής. Η γραμμή του
  // μοντέλου είναι «κοινά τα δεδομένα, ιδιωτικές οι κρίσεις», και μια εκτίμηση
  // συνάφειας με αιτιολόγηση είναι κρίση συγκεκριμένου ανθρώπου για συγκεκριμένη
  // ευκαιρία. Ο πίνακας γεγονότων webhook του `CLAUDE.md` τα ανέφερε· ο κατάλογος
  // εδώ υπερισχύει, και η γραμμή εκεί διορθώθηκε.

  'comment.created': Object.freeze({
    objectType: 'comment',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'resource', 'collection', 'author', 'text', 'createdAt']),
    task: 'P1-3'
  }),

  'rating.created': Object.freeze({
    objectType: 'rating',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze([
      'id', 'resource', 'collection', 'user', 'relevance', 'justification', 'createdAt'
    ]),
    task: 'P1-3'
  }),

  // Ξεχωριστό γεγονός επειδή ο μοναδικός δείκτης της D14 στην τριάδα
  // (resource, user, collection) κάνει τη δεύτερη υποβολή **μεταβολή** και όχι νέα
  // αξιολόγηση. Χωρίς αυτό η ροή θα έδειχνε δύο γνώμες εκεί που υπάρχει μία.
  'rating.updated': Object.freeze({
    objectType: 'rating',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze([
      'id', 'resource', 'collection', 'user', 'relevance', 'justification', 'createdAt'
    ]),
    task: 'P1-3'
  }),

  // --- Καθήκοντα (P1-4) ---------------------------------------------------------

  'task.created': Object.freeze({
    objectType: 'task',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze([
      'id', 'collection', 'resource', 'title', 'description', 'status', 'dueDate',
      'createdBy', 'createdAt'
    ]),
    task: 'P1-4'
  }),

  // Εξωτερικεύσιμο: είναι το γεγονός που ενδιαφέρει τρίτο σύστημα διαχείρισης
  // εργασιών. Το `summary` ονομάζει τους αποδέκτες.
  'task.assigned': Object.freeze({
    objectType: 'task',
    scope: SCOPES.EXTERNALISABLE,
    payload: Object.freeze(['id', 'collection', 'title', 'assignees', 'status', 'dueDate']),
    task: 'P1-4'
  }),

  // Η παλιά και η νέα κατάσταση ζουν στο `summary`. Εδώ μόνο η τρέχουσα.
  'task.status_changed': Object.freeze({
    objectType: 'task',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'collection', 'title', 'status', 'updatedAt']),
    task: 'P1-4'
  }),

  // Ξεχωριστό από το `task.status_changed` **παρότι** είναι ειδική περίπτωσή του:
  // είναι το γεγονός που ένα τρίτο σύστημα θέλει να συνδρομήσει χωρίς να δέχεται
  // κάθε ενδιάμεση μετακίνηση.
  'task.completed': Object.freeze({
    objectType: 'task',
    scope: SCOPES.EXTERNALISABLE,
    payload: Object.freeze(['id', 'collection', 'title', 'status', 'assignees', 'updatedAt']),
    task: 'P1-4'
  }),

  // Το κείμενο της σημείωσης **δεν** είναι στο φορτίο: είναι συνεννόηση μελών.
  'task.note_added': Object.freeze({
    objectType: 'task',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'collection', 'title', 'updatedAt']),
    task: 'P1-4'
  }),

  // **Προστέθηκε με ρητή έγκριση (20/08, P1-4).** Ο πίνακας του §4.5.1 προβλέπει
  // `DELETE /api/tasks/:id`, και ο κατάλογος δεν είχε γεγονός γι' αυτήν: η
  // διαγραφή καθηκόντος θα ήταν η **μόνη** εξαφάνιση οντότητας χωρίς ίχνος, δίπλα
  // στις `resource.deleted`, `collection.deleted`, `webhook.deleted` και
  // `apikey.revoked`. Το επιχείρημα είναι το ίδιο που έβαλε τα κλειδιά μέσα —
  // «κύκλος ζωής χωρίς κανένα ίχνος ήταν κενό της ΜΛΑ-08» — και εδώ είναι
  // **ισχυρότερο**: το κλειδί είναι διαπιστευτήριο, το καθήκον είναι
  // **ανατεθειμένη δουλειά συγκεκριμένων ανθρώπων**.
  //
  // **Εσωτερικό**, κατά τον κανόνα των υπόλοιπων εξαφανίσεων: η δημιουργία και ο
  // εμπλουτισμός εξωτερικεύονται, η εξαφάνιση όχι, γιατί η εξαφάνιση φέρει το
  // κόστος και το κόστος μετριέται σε δουλειά άλλων. Εδώ κυριολεκτικά.
  //
  // **Ο τύπος είναι `task` και όχι `removal`**, παρότι πρόκειται για διαγραφή. Ο
  // τύπος `removal` υπάρχει επειδή ένας πόρος που φεύγει παίρνει μαζί του **ξένα**
  // αντικείμενα, και τα πλήθη τους δεν είναι πεδία του πόρου. Το καθήκον δεν
  // παίρνει τίποτα μαζί του: το κόστος του το εκφράζουν τα **δικά του** πεδία —
  // ποιοι το είχαν αναλάβει και σε ποια κατάσταση εγκαταλείφθηκε.
  'task.deleted': Object.freeze({
    objectType: 'task',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'collection', 'title', 'status', 'assignees']),
    task: 'P1-4'
  }),

  // --- Κλειδιά πρόσβασης (P1-8) -------------------------------------------------
  //
  // **Πάντοτε εσωτερικά**, και ο λόγος είναι δεύτερο σκέλος του κανόνα των
  // κρίσεων. Το πρώτο σκέλος λέει ότι η εξαφάνιση δεν εξωτερικεύεται. Εδώ ισχύει
  // κάτι ισχυρότερο: **γεγονός ασφάλειας δεν εξωτερικεύεται ποτέ**, ούτε η
  // δημιουργία. Η ανακοίνωση σε τρίτο σύστημα ότι εκδόθηκε κλειδί είναι η ίδια η
  // διαρροή — ο παραλήπτης μαθαίνει ότι υπάρχει νέο διαπιστευτήριο και για ποια
  // συλλογή, δηλαδή ακριβώς ό,τι χρειάζεται για να το αναζητήσει.
  //
  // Το φορτίο φέρει τίτλο, συλλογή, δημιουργό και χρόνο. **Ποτέ το μυστικό, ποτέ
  // το αποτύπωμα.**

  'apikey.created': Object.freeze({
    objectType: 'apikey',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'label', 'collection', 'createdBy', 'createdAt']),
    task: 'P1-8'
  }),

  // Η ανάκληση είναι **ανάκληση και όχι διαγραφή**: το έγγραφο μένει με
  // `revoked: true`, ώστε το `createdBy` και το `lastUsedAt` να επιβιώνουν για
  // όποιον ερευνά περιστατικό. Το γεγονός καταγράφει τη στιγμή.
  'apikey.revoked': Object.freeze({
    objectType: 'apikey',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'label', 'collection', 'createdBy', 'createdAt']),
    task: 'P1-8'
  }),

  // **Προστέθηκε με ρητή έγκριση (20/08, Απόφαση D24).** Η ανάκληση δικαιολογείται
  // επειδή το έγγραφο **επιβιώνει**· η διαγραφή σβήνει τη **μοναδική εγγραφή χρήσης
  // που υπάρχει στο σύστημα**, οριστικά και χωρίς αντίγραφο πουθενά. Μετρήθηκε: το
  // `lastUsedAt` γράφεται σε ένα σημείο, με αντικατάσταση, και **καμία εκπομπή δεν
  // γίνεται κατά τη χρήση** — είναι **υψηλή στάθμη και όχι ημερολόγιο**, μία
  // χρονοσφραγίδα που δεν λέει ούτε πόσες φορές ούτε τι ζητήθηκε.
  //
  // Γι' αυτό το φορτίο **φέρει το `lastUsedAt`**, μόνο εδώ από τα τρία γεγονότα του
  // κλειδιού: η διαγραφή είναι η μόνη στιγμή που η τιμή πρόκειται να χαθεί, οπότε
  // είναι η μόνη στιγμή που πρέπει να αντιγραφεί. Το πεδίο υπάρχει ήδη στα ορατά
  // του τύπου `apikey` παραπάνω — δεν χρειάστηκε να το ανοίξει αυτή η προσθήκη.
  //
  // **Εσωτερικό**, κατά τον κανόνα των υπόλοιπων γεγονότων ασφαλείας: γεγονός
  // ασφάλειας δεν εξωτερικεύεται ποτέ, ούτε η δημιουργία.
  'apikey.deleted': Object.freeze({
    objectType: 'apikey',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze([
      'id', 'label', 'collection', 'createdBy', 'createdAt', 'lastUsedAt'
    ]),
    task: 'P1-8'
  }),

  // --- Μέλη και συλλογή (P1-1, οι ενέργειες υπάρχουν ήδη) ----------------------

  'member.added': Object.freeze({
    objectType: 'user',
    scope: SCOPES.EXTERNALISABLE,
    payload: Object.freeze(['id', 'username']),
    task: 'P1-1'
  }),

  // Η αφαίρεση και η μεταβολή ρόλου είναι πληροφορία για συγκεκριμένο πρόσωπο και
  // όχι για τη συλλογή. Το `member.added` λέει ποιος συμμετέχει· τα δύο παρακάτω
  // λένε τι του συνέβη.
  'member.removed': Object.freeze({
    objectType: 'user',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'username']),
    task: 'P1-1'
  }),

  'member.role_changed': Object.freeze({
    objectType: 'user',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'username']),
    task: 'P1-1'
  }),

  // --- Δημιουργία και διαγραφή: τα τέσσερα που έλειπαν ------------------------
  //
  // Προστέθηκαν αφού ο στατικός φράχτης πληρότητας τα ονόμασε. Ο κατάλογος είχε
  // δηλωθεί **πλήρης** και γραφτεί πριν χωριστούν οι κλάδοι ακριβώς για να είναι η
  // μοναδική πηγή — και του έλειπε η κατηγορία με τη **μεγαλύτερη ανάγκη ίχνους**.
  //
  // Η απουσία δεν ήταν ατέλεια αλλά **αντίφαση με τη D13**: σε σύστημα όπου η
  // διαγραφή παίρνει μαζί της τις κρίσεις άλλων ομάδων, η ίδια η διαγραφή ήταν το
  // μόνο πράγμα που δεν άφηνε πουθενά αποτύπωμα.
  //
  // **Και τα τρία είναι εσωτερικά.** Η αιτιολόγηση ανά γεγονός παρακάτω· η κοινή
  // γραμμή είναι ότι η δημιουργία και ο εμπλουτισμός εξωτερικεύονται, η **εξαφάνιση**
  // όχι — γιατί η εξαφάνιση φέρει το κόστος, και το κόστος μετριέται σε δουλειά
  // άλλων.

  // Εσωτερικό για δομικό λόγο, όχι για λόγο πολιτικής: η δρομολόγηση των συνδρομών
  // γίνεται με ακριβή ισότητα στη συλλογή του γεγονότος, και **καμία συνδρομή δεν
  // μπορεί να προϋπάρχει για συλλογή που μόλις δημιουργήθηκε**. Δηλωμένο ως
  // εξωτερικεύσιμο, θα ήταν νεκρός κώδικας που υπόσχεται παράδοση η οποία δεν
  // γίνεται ποτέ — χειρότερο από ρητή άρνηση.
  'collection.created': Object.freeze({
    objectType: 'collection',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze([
      'id', 'title', 'description', 'topic', 'visibility', 'createdBy', 'createdAt'
    ]),
    task: 'P1-1'
  }),

  // Εσωτερικό, επίσης για δομικό λόγο: ο διανομέας **φορτώνει τη συλλογή** για να
  // κρίνει ορατότητα και ρόλο κατόχου. Σε διαγραμμένη συλλογή το ερώτημα επιστρέφει
  // κενό και η παράδοση σταματά εκεί. Το γεγονός είναι επομένως δομικά
  // ανεπίδοτο, και η δήλωσή του ως εξωτερικεύσιμου θα ήταν ψευδής υπόσχεση.
  //
  // Το τίμημα δηλώνεται: ένα τρίτο σύστημα που αντιγράφει μια συλλογή **δεν
  // μαθαίνει ποτέ ότι έπαψε να υπάρχει**. Η ροή του απλώς σιωπά. Η διέξοδος είναι
  // ανάγνωση από τη διεπαφή προγραμματισμού, όχι ειδοποίηση.
  'collection.deleted': Object.freeze({
    objectType: 'removal',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze([
      'id', 'title', 'deletedResources', 'deletedComments', 'deletedRatings'
    ]),
    task: 'P1-1'
  }),

  // --- Ενέργειες ασφαλείας (P1-1) ----------------------------------------------
  //
  // **Γεγονός ασφαλείας δεν εξωτερικεύεται ποτέ.** Η αποστολή σε τρίτο σύστημα του
  // «ποιος έγινε διαχειριστής» ή του «ποια συνδρομή δημιουργήθηκε» δίνει σε
  // παραλήπτη εκτός συστήματος τον χάρτη των προνομίων του — δηλαδή ακριβώς την
  // πληροφορία που χρειάζεται όποιος θέλει να τα καταχραστεί. Η κατηγορία είναι
  // εσωτερική **χωρίς εξέταση ορατότητας**: δεν υπάρχει συλλογή που να τα κάνει
  // δημόσια, γιατί ο καθολικός ρόλος δεν ανήκει σε καμία.
  //
  // **Γιατί προστέθηκαν.** Η σάρωση για σχόλια που είχαν γίνει ψευδή βρήκε γραμμή
  // που βεβαίωνε ότι οι χρήστες και οι συνδρομές «δεν έχουν —ούτε πρέπει να
  // έχουν— γεγονός». Το πρώτο σκέλος ήταν περιγραφή και ίσχυε· το δεύτερο ήταν
  // **νόρμα** και δεν άντεχε: η προαγωγή σε καθολικό διαχειριστή δεν άφηνε κανένα
  // ίχνος, ενώ η έκδοση κλειδιού —που περιορίζεται σε **μία** συλλογή— άφηνε δύο.
  // Σύστημα που καταγράφει το μικρότερο δικαίωμα και σιωπά στο μεγαλύτερο έχει την
  // προτεραιότητα ανάποδα.

  'user.promoted': Object.freeze({
    objectType: 'user',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'username']),
    task: 'P1-1'
  }),

  // ⚠ **ΛΑ-01, και είναι η ΜΟΝΗ εγγραφή του καταλόγου που γεννιέται από
  // ανώνυμο αίτημα.** Η επαναφορά ξεκινά **χωρίς** συνδεδεμένο χρήστη, οπότε ο
  // «δράστης» είναι ο ίδιος ο κάτοχος του λογαριασμού.
  //
  // **Εσωτερικό, χωρίς εξαίρεση.** Η αλλαγή κωδικού είναι γεγονός ασφαλείας ενός
  // προσώπου· δεν έχει καμία δουλειά σε τρίτο σύστημα, όσο κι αν το ζητήσει
  // κάποιος. Το φορτίο **δεν** περιέχει ούτε το διακριτικό ούτε τη διεύθυνση
  // ηλεκτρονικού ταχυδρομείου: το πρώτο θα ήταν καταστροφικό, το δεύτερο
  // περιττό — ο μόνος παραλήπτης τη γνωρίζει ήδη.
  'user.password_reset': Object.freeze({
    objectType: 'user',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'username']),
    task: 'P1-9'
  }),

  // ⚠ **ΛΑ-02 / D45.** Δεύτερη εγγραφή ασφαλείας ενός προσώπου, δίπλα στην
  // επαναφορά κωδικού και με **την ίδια αιτιολόγηση**: το ίχνος «η διεύθυνσή σου
  // άλλαξε» είναι ακριβώς εκείνο που θέλει να δει όποιος **δεν** το έκανε.
  //
  // **Εσωτερικό, χωρίς εξαίρεση**, και το φορτίο **δεν περιέχει καμία από τις δύο
  // διευθύνσεις** — ούτε την παλιά ούτε τη νέα. Ο μόνος παραλήπτης τις γνωρίζει
  // ήδη· οποιοσδήποτε άλλος δεν πρέπει. Η διεύθυνση είναι το **αναγνωριστικό
  // σύνδεσης** του λογαριασμού, οπότε σε φορτίο γεγονότος θα ήταν μισό
  // διαπιστευτήριο.
  'user.email_changed': Object.freeze({
    objectType: 'user',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'username']),
    task: 'P1-10'
  }),

  'user.demoted': Object.freeze({
    objectType: 'user',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'username']),
    task: 'P1-1'
  }),

  // Η συνδρομή φέρει **μυστικό υπογραφής** και ανοίγει δίαυλο προς τρίτο σύστημα.
  // Είναι η ίδια κατηγορία με το κλειδί πρόσβασης, με μία διαφορά που τη βαραίνει:
  // το κλειδί επιτρέπει σε κάποιον να **διαβάσει** όταν το ζητήσει, ενώ η συνδρομή
  // **στέλνει μόνη της**, συνεχώς, χωρίς να χρειάζεται κανείς να ζητήσει τίποτα.
  //
  // Το έγγραφο της συνδρομής καταγράφει την **τρέχουσα κατάσταση**, όχι το
  // ιστορικό: συνδρομή που δημιουργήθηκε και διαγράφηκε δεν αφήνει τίποτα. Οι
  // εγγραφές παράδοσης δείχνουν **τι έφυγε**, ποτέ **ποιος το επέτρεψε**.
  //
  // Η ενημέρωση περιλαμβάνεται γιατί αλλάζει τη διεύθυνση ή τον κατάλογο
  // γεγονότων: είναι μεταβολή του **τι φεύγει και πού**, όχι λεπτομέρεια.
  'webhook.created': Object.freeze({
    objectType: 'webhook',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'url', 'events', 'collection', 'active']),
    task: 'P2-1'
  }),

  'webhook.updated': Object.freeze({
    objectType: 'webhook',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'url', 'events', 'collection', 'active']),
    task: 'P2-1'
  }),

  'webhook.deleted': Object.freeze({
    objectType: 'webhook',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'url', 'events', 'collection']),
    task: 'P2-1'
  }),

  'collection.archived': Object.freeze({
    objectType: 'collection',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'title', 'archived', 'archivedAt']),
    task: 'P1-1'
  }),

  'collection.unarchived': Object.freeze({
    objectType: 'collection',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'title', 'archived', 'archivedAt']),
    task: 'P1-1'
  }),

  'collection.updated': Object.freeze({
    objectType: 'collection',
    scope: SCOPES.INTERNAL,
    payload: Object.freeze(['id', 'title', 'description', 'topic', 'visibility', 'updatedAt']),
    task: 'P1-1'
  })
});

/** Όλοι οι τύποι, σταθερή σειρά. Η μόνη επιτρεπτή πηγή ονομάτων γεγονότων. */
export const EVENT_TYPES = Object.freeze(Object.keys(EVENT_CATALOG));

/** Οι τύποι που μπορεί ποτέ να φύγουν: ο κατάλογος επιλογών μιας εγγραφής webhook. */
export const EXTERNALISABLE_EVENTS = Object.freeze(
  EVENT_TYPES.filter((type) => EVENT_CATALOG[type].scope === SCOPES.EXTERNALISABLE)
);

export const isKnownEvent = (type) => Object.hasOwn(EVENT_CATALOG, type);

/**
 * Επιτρέπεται να φύγει **αυτό** το γεγονός σε **αυτόν** τον παραλήπτη;
 *
 * Δύο φράχτες, όχι ένας. Ο πρώτος είναι ο τύπος: εσωτερικό δεν φεύγει ποτέ. Ο
 * δεύτερος είναι η ορατότητα της συλλογής: δημόσια φεύγει, ιδιωτική μόνο αν ο
 * κάτοχος της εγγραφής webhook είναι **συντονιστής της ίδιας** συλλογής.
 *
 * Ζει εδώ και όχι στο P2-1 για τον λόγο που υπάρχει ολόκληρο το αρχείο: ο κανόνας
 * γραμμένος δεύτερη φορά είναι κανόνας που θα αποκλίνει. Δεν αποστέλλει τίποτα —
 * είναι καθαρή συνάρτηση, όπως το `lib/rbac.js`.
 */
export function mayLeaveSystem(type, { visibility, subscriberIsCoordinator = false } = {}) {
  const event = EVENT_CATALOG[type];
  if (!event || event.scope !== SCOPES.EXTERNALISABLE) return false;
  if (visibility === 'public') return true;
  return subscriberIsCoordinator === true;
}

export default {
  SCOPES,
  VISIBLE_FIELDS,
  EVENT_CATALOG,
  EVENT_TYPES,
  EXTERNALISABLE_EVENTS,
  isKnownEvent,
  mayLeaveSystem
};
